import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useAppStore } from '../store/appStore';
import { QuickReminderModal } from './QuickReminderModal';
import { QuickAddToListModal } from './QuickAddToListModal';
import { UserAssignmentModal } from './UserAssignmentModal';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

export interface ContactQuickActionsProps {
  contactId: string;
  contactName: string;
  phoneNumber?: string;
  onNotify: (type: 'success' | 'error', title: string, message: string) => void;
  onListMembershipChange?: () => void;
  onAssignmentChange?: () => void;
  /** A reminder was created (e.g. to log it on the visit in progress). */
  onReminderCreated?: () => void;
  /** Smaller icon-only circles with no label beneath — for placing this row
   * alongside other actions (e.g. Save/Delete) in a single compact header
   * bar, where the normal icon+label layout doesn't have room. */
  compact?: boolean;
  /** Overrides the actions row style (e.g. dropping its bottom margin inside a card). */
  rowStyle?: StyleProp<ViewStyle>;
}

/**
 * Reminder / Add to List / Assign row — originally the call-end screen's
 * quick actions (PhoneCallScreen.tsx), extracted so the same row can also
 * appear on the contact edit view without duplicating the modal wiring.
 */
export const ContactQuickActions: React.FC<ContactQuickActionsProps> = ({ contactId, contactName, phoneNumber, onNotify, onListMembershipChange, onAssignmentChange, onReminderCreated, compact = false, rowStyle }) => {
  const styles = useThemedStyles(getStyles);
  const { user, bumpContactsRefreshToken } = useAppStore();
  const [reminderModalVisible, setReminderModalVisible] = useState(false);
  const [addToListModalVisible, setAddToListModalVisible] = useState(false);
  const [assignmentModalVisible, setAssignmentModalVisible] = useState(false);

  return (
    <>
      <View style={[styles.quickActionsRow, compact && styles.quickActionsRowCompact, rowStyle]}>
        <TouchableOpacity style={styles.quickActionButton} onPress={() => setReminderModalVisible(true)} activeOpacity={0.75}>
          <View style={[styles.quickActionIconWrap, compact && styles.quickActionIconWrapCompact]}>
            <Ionicons name="alarm-outline" size={compact ? 16 : 20} color={colors.primary as string} />
          </View>
          {!compact && <Text style={styles.quickActionLabel}>Reminder</Text>}
        </TouchableOpacity>
        <TouchableOpacity style={styles.quickActionButton} onPress={() => setAddToListModalVisible(true)} activeOpacity={0.75}>
          <View style={[styles.quickActionIconWrap, compact && styles.quickActionIconWrapCompact]}>
            <Ionicons name="list-outline" size={compact ? 16 : 20} color={colors.primary as string} />
          </View>
          {!compact && <Text style={styles.quickActionLabel}>Add to List</Text>}
        </TouchableOpacity>
        <TouchableOpacity style={styles.quickActionButton} onPress={() => setAssignmentModalVisible(true)} activeOpacity={0.75}>
          <View style={[styles.quickActionIconWrap, compact && styles.quickActionIconWrapCompact]}>
            <Ionicons name="person-add-outline" size={compact ? 16 : 20} color={colors.primary as string} />
          </View>
          {!compact && <Text style={styles.quickActionLabel}>Assign</Text>}
        </TouchableOpacity>
      </View>

      <QuickReminderModal
        visible={reminderModalVisible}
        onClose={() => setReminderModalVisible(false)}
        contactName={contactName}
        contactPhoneNumber={phoneNumber}
        onNotify={(type, title, message) => {
          onNotify(type, title, message);
          if (type === 'success') onReminderCreated?.();
        }}
      />
      <QuickAddToListModal
        visible={addToListModalVisible}
        onClose={() => setAddToListModalVisible(false)}
        contactId={contactId}
        contactName={contactName}
        currentUserId={user?.id}
        onAdded={() => {
          bumpContactsRefreshToken();
          onListMembershipChange?.();
        }}
        onNotify={onNotify}
      />
      <UserAssignmentModal
        visible={assignmentModalVisible}
        onClose={() => setAssignmentModalVisible(false)}
        contactId={contactId}
        contactName={contactName}
        onAssignmentChange={() => {
          setAssignmentModalVisible(false);
          onAssignmentChange?.();
        }}
      />
    </>
  );
};

const getStyles = () => StyleSheet.create({
  quickActionsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: spacing.lg,
  },
  quickActionsRowCompact: {
    justifyContent: 'flex-start',
    gap: spacing.sm,
    marginBottom: 0,
  },
  quickActionButton: {
    alignItems: 'center',
    gap: spacing.xs,
  },
  quickActionIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: withAlpha(colors.primary, 0.08),
  },
  quickActionIconWrapCompact: {
    width: 30,
    height: 30,
    borderRadius: 15,
  },
  quickActionLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary as string,
    fontWeight: '600' as const,
  },
});
